import {
    Box,
    Button,
    Checkbox,
    Flex,
    HStack,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    VStack,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { RiSearchLine } from 'react-icons/ri'

interface ProviderSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (selectedProviders: number[]) => void
  providerOptions: { label: string; value: number }[]
  selectedProviders: number[]
}

export function ProviderSelectionModal({
  isOpen,
  onClose,
  onConfirm,
  providerOptions,
  selectedProviders,
}: ProviderSelectionModalProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [tempSelectedProviders, setTempSelectedProviders] = useState<number[]>([])

  useEffect(() => {
    if (isOpen) {
      setTempSelectedProviders(selectedProviders)
      setSearchTerm('')
    }
  }, [isOpen, selectedProviders])

  const filteredProviders = providerOptions.filter((provider) =>
    provider.label.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const handleToggleProvider = (providerId: number) => {
    setTempSelectedProviders((prev) =>
      prev.includes(providerId) ? prev.filter((id) => id !== providerId) : [...prev, providerId],
    )
  }

  const handleSelectAll = () => {
    if (tempSelectedProviders.length === filteredProviders.length) {
      setTempSelectedProviders([])
    } else {
      setTempSelectedProviders(filteredProviders.map((provider) => provider.value))
    }
  }

  const handleConfirm = () => {
    onConfirm(tempSelectedProviders)
    onClose()
  }

  const handleCancel = () => {
    setTempSelectedProviders(selectedProviders)
    onClose()
  }

  const isAllSelected = filteredProviders.length > 0 && tempSelectedProviders.length === filteredProviders.length
  const isSomeSelected = tempSelectedProviders.length > 0 && tempSelectedProviders.length < filteredProviders.length

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white" {...({} as any)}>
        <ModalHeader>
          <Text fontSize="lg" fontWeight="bold">
            Selecionar Fornecedores
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Campo de busca */}
            <Box>
              <Text fontSize="sm" color="gray.400" mb={2}>
                Buscar fornecedor
              </Text>
              <Input
                placeholder="Digite o nome do fornecedor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg="gray.700"
                color="white"
                borderColor="gray.600"
                _hover={{ borderColor: 'pink.400' }}
                _focus={{ borderColor: 'pink.400', boxShadow: 'none' }}
                leftIcon={<RiSearchLine />}
                {...({} as any)}
              />
            </Box>

            {/* Selecionar todos */}
            {filteredProviders.length > 0 && (
              <HStack
                p={2}
                bg="gray.700"
                borderRadius="md"
                cursor="pointer"
                onClick={handleSelectAll}
                _hover={{ bg: 'gray.600' }}
                {...({} as any)}>
                <Checkbox
                  isChecked={isAllSelected}
                  isIndeterminate={isSomeSelected}
                  onChange={handleSelectAll}
                  colorScheme="pink"
                  {...({} as any)}
                />
                <Text fontSize="sm" fontWeight="medium">
                  {isAllSelected ? 'Desmarcar todos' : 'Selecionar todos'}
                </Text>
              </HStack>
            )}

            {/* Lista de fornecedores */}
            <Box maxHeight="300px" overflowY="auto">
              <VStack spacing={0} align="stretch">
                {filteredProviders.length === 0 ? (
                  <Text color="gray.400" textAlign="center" py={4}>
                    {searchTerm ? 'Nenhum fornecedor encontrado' : 'Nenhum fornecedor disponível'}
                  </Text>
                ) : (
                  filteredProviders.map((provider) => (
                    <HStack
                      key={provider.value}
                      p={3}
                      _hover={{ bg: 'gray.600' }}
                      cursor="pointer"
                      onClick={() => handleToggleProvider(provider.value)}
                      borderRadius="md"
                      {...({} as any)}>
                      <Checkbox
                        isChecked={tempSelectedProviders.includes(provider.value)}
                        onChange={() => handleToggleProvider(provider.value)}
                        colorScheme="pink"
                        {...({} as any)}
                      />
                      <Text color="white" fontSize="sm">
                        {provider.label}
                      </Text>
                    </HStack>
                  ))
                )}
              </VStack>
            </Box>

            {/* Contador de selecionados */}
            {tempSelectedProviders.length > 0 && (
              <Text fontSize="sm" color="pink.400" textAlign="center">
                {tempSelectedProviders.length} fornecedor(es) selecionado(s)
              </Text>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Flex gap={2} width="100%">
            <Button variant="ghost" onClick={handleCancel} flex={1}>
              Cancelar
            </Button>
            <Button colorScheme="pink" onClick={handleConfirm} flex={1}>
              OK
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
